import type { MarketplaceOrderHandoffSnapshot } from "@nexus/contracts";
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Icon,
} from "@nexus/ui";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import {
  VendorEmailFrame,
  type VendorEmailViewMode,
} from "@/features/marketplace/components/vendor-email/VendorEmailFrame";
import { buildVendorEmailEml } from "@/features/marketplace/lib/vendor-email-eml";
import { buildVendorEmailMailtoHref } from "@/features/marketplace/lib/vendor-email-mailto";
import { useCopyToClipboard } from "@/hooks/useCopyToClipboard";

export function VendorEmailDialog({
  snapshot,
  orderId,
  sentByUserName,
  open,
  onOpenChange,
}: {
  snapshot: MarketplaceOrderHandoffSnapshot;
  orderId: string;
  sentByUserName?: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { t, i18n } = useTranslation();
  const [view, setView] = useState<VendorEmailViewMode>("desktop");
  const [versionIndex, setVersionIndex] = useState(-1);
  const { copy } = useCopyToClipboard({
    onCopy: () =>
      toast.success(t("marketplace.orders.list.brief.copied", "Copied")),
  });
  const versions = snapshot.email.versions ?? [];
  const selected = versionIndex >= 0 ? versions[versionIndex] : null;
  const html = selected?.html ?? snapshot.email.html;
  const text = selected?.text ?? snapshot.email.text;
  const subject = selected?.subject ?? snapshot.email.subject ?? "";
  const from = selected?.from ?? snapshot.email.from;
  const replyTo = selected?.replyTo ?? snapshot.email.replyTo;
  const to = selected?.to ?? snapshot.email.to ?? [];
  const cc = selected?.cc ?? snapshot.email.cc ?? [];
  const sentAt = selected?.sentAt ?? snapshot.email.sentAt ?? snapshot.sentAt;
  const sentBy = selected?.sentBy ?? snapshot.email.sentBy ?? sentByUserName;
  const sentLabel = sentAt
    ? new Intl.DateTimeFormat(i18n.language, {
        dateStyle: "medium",
        timeStyle: "short",
      }).format(new Date(sentAt))
    : null;
  const isLegacy = snapshot.email.isLegacy || !snapshot.email.recorded;

  function downloadEml() {
    const eml = buildVendorEmailEml({
      from,
      to,
      cc,
      subject,
      html: html ?? "",
      text: text ?? "",
      sentAt,
    });
    const blob = new Blob([eml], { type: "message/rfc822" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `partner-email-${orderId.slice(-6)}.eml`;
    link.click();
    URL.revokeObjectURL(url);
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        variant="form"
        className="flex h-[min(92vh,880px)] w-full flex-col overflow-hidden sm:max-w-[min(960px,calc(100vw-2rem))]"
      >
        <DialogHeader className="shrink-0">
          <DialogTitle>
            {t("marketplace.orders.list.brief.emailTitle", "Partner email")}
          </DialogTitle>
          <DialogDescription>
            {[
              sentLabel
                ? t("marketplace.orders.list.brief.emailSentMeta", {
                    date: sentLabel,
                    sender: sentBy ?? "",
                    defaultValue: "Sent {{date}} · by {{sender}}",
                  })
                : t(
                    "marketplace.orders.list.brief.emailNotRecorded",
                    "The rendered partner email was not stored for this order.",
                  ),
              snapshot.email.deliveryStatus
                ? t("marketplace.orders.list.brief.deliveryStatus", {
                    status: snapshot.email.deliveryStatus,
                    defaultValue: "Delivery: {{status}}",
                  })
                : null,
            ]
              .filter(Boolean)
              .join(" · ")}
          </DialogDescription>
        </DialogHeader>
        {versions.length > 1 ? (
          <label className="flex items-center gap-2 text-xs text-muted-foreground">
            {t("marketplace.orders.list.brief.versionLabel", {
              count: versions.length,
              defaultValue: "Sent {{count}} times · viewing",
            })}
            <select
              className="h-8 rounded-md border border-border bg-background px-2 text-foreground"
              value={versionIndex}
              onChange={(event) => setVersionIndex(Number(event.target.value))}
            >
              <option value={-1}>
                {t("marketplace.orders.list.brief.versionLatest", "latest")}
              </option>
              {versions.map((version, index) => (
                <option key={version.version} value={index}>
                  {t("marketplace.orders.list.brief.versionItem", {
                    version: version.version,
                    defaultValue: "Version {{version}}",
                  })}
                </option>
              ))}
            </select>
          </label>
        ) : null}
        <div className="flex min-h-0 w-full flex-1 flex-col overflow-hidden">
          <VendorEmailFrame
            from={from}
            replyTo={replyTo}
            to={to.join(", ")}
            cc={cc.join(", ")}
            subject={subject}
            html={html}
            text={text}
            view={view}
            onViewChange={setView}
            isLegacy={isLegacy}
            iframeTitle={t(
              "marketplace.orders.list.brief.emailTitle",
              "Partner email",
            )}
          />
        </div>
        <DialogFooter className="shrink-0 !bg-muted sm:justify-between">
          <div className="flex flex-wrap items-center gap-0.5">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="text-muted-foreground"
              iconStart="copy"
              onClick={() =>
                void copy(view === "plain" ? (text ?? "") : (html ?? ""))
              }
            >
              {t("marketplace.orders.list.brief.copyEmail", "Copy")}
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="text-muted-foreground"
              iconStart="download"
              onClick={downloadEml}
            >
              {t("marketplace.orders.list.brief.downloadEml", "Download .eml")}
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="text-muted-foreground"
              asChild
            >
              <a
                href={buildVendorEmailMailtoHref({
                  to,
                  cc,
                  subject,
                  body: text ?? "",
                  truncatedNote: t(
                    "marketplace.orders.list.brief.forwardMailtoTruncated",
                    "Download the .eml for the full partner email.",
                  ),
                })}
              >
                <Icon name="mail" color="inherit" size="sm" />
                {t("marketplace.orders.list.brief.forward", "Forward…")}
              </a>
            </Button>
          </div>
          <div className="flex flex-wrap items-center justify-end gap-2">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => onOpenChange(false)}
            >
              {t("common.close", "Close")}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
